import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import http from "../api/http";
import Category from "../types/category";
import SearchResults from "../types/searchResults";
import SearchParams, { defaultParams } from "../types/searchParams";
import getSearchQueryString from "../helpers/getSearchQueryString";

interface CategoryState {
  category: Category;
  categorySearchResults?: SearchResults;
  categoryFilter: SearchParams;
  jobTypeCategories: Category[];
  status: "idle" | "loading" | "failed";
}

const initialState: CategoryState = {
  category: {
    id: 0,
    name: "",
    jobType: undefined,
  },
  categoryFilter: defaultParams,
  categorySearchResults: undefined,
  jobTypeCategories: [],
  status: "idle",
};

export const getCategoryId = createAsyncThunk<Category, number>(
  "category/get/id",
  async (id: number) => {
    const response = await http.get<Category>(`/category/${id}`);
    return response.data;
  }
);

export const editCategory = createAsyncThunk<Category, Category>(
  "category/edit",
  async (category: Category) => {
    const response = await http.put<Category>(
      `/category/${category.id}`,
      category,
      {
        params: {
          successToast: true,
        },
      }
    );
    return response.data;
  }
);

export const deleteCategory = createAsyncThunk<Category, number>(
  "category/delete",
  async (id: number) => {
    const response = await http.delete<Category>(`/category/${id}`, {
      params: {
        successToast: true,
      },
    });
    return response.data;
  }
);

export const postCategory = createAsyncThunk<Category, Category>(
  "category/post",
  async (category: Category) => {
    const response = await http.post<Category>(`/category`, category, {
      params: {
        successToast: true,
      },
    });
    return response.data;
  }
);

export const searchCategories = createAsyncThunk<SearchResults, SearchParams>(
  "categories/search",
  async (search: SearchParams) => {
    let qs = getSearchQueryString(search);
    const response = await http.get<SearchResults>(`category/search?${qs}`);
    return response.data;
  }
);

export const getJobTypeCategories = createAsyncThunk<Category[], number>(
  "categories/jobtype/get",
  async (id: number) => {
    const response = await http.get<Category[]>(`category/jobtype/${id}`);
    return response.data;
  }
);

const categorySlice = createSlice({
  name: "category",
  initialState,
  reducers: {
    resetCategory(state) {
      state.category = { ...initialState.category };
    },
    setCategoryFilter(state, action) {
      state.categoryFilter = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCategoryId.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getCategoryId.fulfilled, (state, action) => {
        state.status = "idle";
        state.category = action.payload;
      })
      .addCase(getCategoryId.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(editCategory.pending, (state) => {
        state.status = "loading";
      })
      .addCase(editCategory.fulfilled, (state, action) => {
        state.status = "idle";
      })
      .addCase(editCategory.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(deleteCategory.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.status = "idle";
      })
      .addCase(deleteCategory.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(postCategory.pending, (state) => {
        state.status = "loading";
      })
      .addCase(postCategory.fulfilled, (state, action) => {
        state.status = "idle";
      })
      .addCase(postCategory.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(searchCategories.pending, (state) => {
        state.status = "loading";
      })
      .addCase(searchCategories.fulfilled, (state, action) => {
        state.status = "idle";
        state.categorySearchResults = action.payload;
      })
      .addCase(searchCategories.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(getJobTypeCategories.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getJobTypeCategories.fulfilled, (state, action) => {
        state.status = "idle";
        state.jobTypeCategories = action.payload;
      })
      .addCase(getJobTypeCategories.rejected, (state) => {
        state.status = "failed";
      });
  },
});

export default categorySlice.reducer;
export const { resetCategory, setCategoryFilter } = categorySlice.actions;
